import React, {useState, useCallback} from 'react';
import {JsonEditor as JsonEditReact} from 'json-edit-react';
import {Box, TextField, ToggleButtonGroup, ToggleButton, Alert, Stack} from '@mui/material';
import AccountTreeIcon from '@mui/icons-material/AccountTree';
import CodeIcon from '@mui/icons-material/Code';

const JsonEditor = ({value, onChange, disabled = false, rootName = 'root', collapsed = 1}) => {
    const [mode, setMode] = useState('tree');
    const [textValue, setTextValue] = useState(() => JSON.stringify(value, null, 2));
    const [textError, setTextError] = useState(null);

    const handleModeChange = useCallback((_, newMode) => {
        if (!newMode) return;
        if (newMode === 'text') {
            setTextValue(JSON.stringify(value, null, 2));
            setTextError(null);
        } else if (newMode === 'tree' && mode === 'text') {
            try {
                const parsed = JSON.parse(textValue);
                onChange(parsed);
                setTextError(null);
            } catch (e) {
                setTextError('Fix JSON errors before switching to Tree view');
                return;
            }
        }
        setMode(newMode);
    }, [value, textValue, mode, onChange]);

    const handleTextChange = useCallback((e) => {
        const raw = e.target.value;
        setTextValue(raw);
        try {
            const parsed = JSON.parse(raw);
            setTextError(null);
            onChange(parsed);
        } catch {
            setTextError('Invalid JSON');
        }
    }, [onChange]);

    return (
        <Box>
            <Stack direction="row" spacing={2} alignItems="center" sx={{mb: 1.5}}>
                <ToggleButtonGroup value={mode} exclusive onChange={handleModeChange} size="small">
                    <ToggleButton value="tree"><AccountTreeIcon sx={{mr: 0.5}}/> Tree</ToggleButton>
                    <ToggleButton value="text"><CodeIcon sx={{mr: 0.5}}/> Text</ToggleButton>
                </ToggleButtonGroup>
            </Stack>

            {textError && mode === 'text' && <Alert severity="error" sx={{mb: 1}}>{textError}</Alert>}

            {mode === 'tree' ? (
                <Box sx={{
                    '& .jer-editor-container': {fontFamily: 'monospace', fontSize: '0.875rem'},
                }}>
                    <JsonEditReact
                        data={value}
                        setData={onChange}
                        rootName={rootName}
                        collapse={collapsed}
                        restrictEdit={disabled}
                        restrictDelete={disabled}
                        restrictAdd={disabled}
                        restrictTypeSelection={disabled}
                        minWidth="100%"
                    />
                </Box>
            ) : (
                <TextField
                    fullWidth
                    multiline
                    minRows={6}
                    maxRows={30}
                    value={textValue}
                    onChange={handleTextChange}
                    disabled={disabled}
                    error={!!textError}
                    slotProps={{input: {sx: {fontFamily: 'monospace', fontSize: '0.875rem'}}}}
                />
            )}
        </Box>
    );
};

export default JsonEditor;
